import React, { useEffect, useRef, useState } from "react";
import NavigationComponent from "../../calendar/components/NavigationComponent";
import DateInput from "../../../../parts/inputs/date-input";
import submit from "submit-form";
import moment from "moment";
import _ from "lodash";
import ToolTip from "../../../../parts/tooltip";
// import { useHorizontalScroll } from "../../../../hooks/useHorizontalScroll";
import {
  getBlockData,
  getMetricsData,
  getPeriods,
} from "../../../../utils/block-review-helpers";
import { MUSCLE_GROUP_ARRAY } from "../../../../constants";
import SvgIcon from "../../../../parts/SvgIcon";
import FilterChips from "../../calendar/FilterChips";

const BlockReview = ({ locals }) => {
  const {
    query,
    pathname,
    weeksExercises,
    activeUser,
    activeUserToRequest,
    withWeight,
    fromMetric,
  } = locals;

  const athleteId = locals.athlete ? locals.athlete._id : locals.activeUser._id;
  const { units } = activeUser;
  const precision = units === "metric" ? 2 : 1;
  const unitsLabel = units === "metric" ? "kg" : "lb";

  const from = query.from
    ? query.from
    : moment().subtract(4, "weeks").format("YYYY-MM-DD");
  const to = query.to ? query.to : moment().format("YYYY-MM-DD");

  const startingCalendarDay = activeUser.startingCalendarDay || 0;

  const [filteredWeeks, setFilteredWeeks] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [exerciseBlockData, setExerciseBlockData] = useState([]);
  const [exerciseBlockDataToShow, setExerciseBlockDataToShow] = useState([]);
  const [metricsBlockData, setMetricsBlockData] = useState({});

  const metricsDataKeys = Object.keys(metricsBlockData).sort();

  const intensityMeasure =
    _.get(activeUserToRequest, "intensityMeasure", "rpe") === "rpe"
      ? "RPE"
      : "RiR";

  const ref = useRef(null);
  const [left, setLeft] = useState(0);

  // useEffect to reject the user from entering the report while responsive is not ready
  useEffect(() => {
    if (window.innerWidth <= 960) {
      history.back();
    }
  }, []);

  useEffect(() => {
    if (ref.current) {
      const firstChildWidth = ref.current.offsetWidth;

      if (firstChildWidth) {
        setLeft(firstChildWidth);
      }
    }
  }, [exerciseBlockData]);

  useEffect(() => {
    // Filter out exercises without outcomes
    const filteredArray = weeksExercises.filter(
      (item) => item.sets && item.sets.some((set) => set.outcomes)
    );

    const sortedArray = filteredArray.sort((a, b) => a.utcDate - b.utcDate);

    setFilteredWeeks(sortedArray);
  }, [weeksExercises]);

  useEffect(() => {
    if (filteredWeeks.length > 0) {
      const blockData = getBlockData(
        filteredWeeks,
        from,
        to,
        startingCalendarDay,
        intensityMeasure,
        activeUser,
        withWeight,
        fromMetric
      );

      const metricsData = getMetricsData(
        filteredWeeks,
        startingCalendarDay,
        from,
        to
      );

      const periods = getPeriods(from, to, startingCalendarDay);

      setPeriods(periods);
      setExerciseBlockData(blockData);
      setExerciseBlockDataToShow(blockData);
      setMetricsBlockData(metricsData);

      // Filters the exercises
      // Put in here because outside the useEffect would cause sync issues
      if (
        query.exerciseFilters &&
        JSON.parse(query.exerciseFilters).length > 0
      ) {
        const newData = blockData.filter((exercise) =>
          JSON.parse(query.exerciseFilters).includes(exercise.name)
        );

        const metricsData = getMetricsData(
          filteredWeeks,
          startingCalendarDay,
          from,
          to,
          JSON.parse(query.exerciseFilters)
        );

        setMetricsBlockData(metricsData);
        setExerciseBlockDataToShow(newData);
      }
    } else {
      setPeriods([]);
      setExerciseBlockData([]);
      setExerciseBlockDataToShow([]);
      setMetricsBlockData({});
    }
  }, [filteredWeeks, query.exerciseFilters]);

  const handleDateChange = (date, field) => {
    submit("/actions/refresh", {
      method: "GET",
      body: { [field]: date },
    });
  };

  const handleActiveTab = (tab) => {
    setTabState(tab);
    setDateRange(tab);
  };

  //start date
  const [startDateToShow, setStartDateToShow] = useState(
    query.from
      ? moment(query.from)
      : moment().subtract(4, "weeks").format("MMM DD, YYYY")
  );

  //end state
  const [endDateToShow, setEndDateToShow] = useState(
    query.to ? moment(query.to) : moment()
  );

  //control the date range tab state (1 month, 3 months... lifetime...)
  const [tabState, setTabState] = useState(
    query.activeRange ? query.activeRange : 4
  );

  const setDateRange = (tab) => {
    const today = moment();
    const lifetimeDate = "2015-12-31T07:57:45.121Z"; //date when MSB starts
    const weeksToSubtract = Number(tab);
    const periodDate = weeksToSubtract
      ? moment().subtract(weeksToSubtract, "weeks")
      : moment(lifetimeDate);
    const startFormat = periodDate.format("YYYY-MM-DD");
    const endFormat = today.format("YYYY-MM-DD");
    setStartDateToShow(periodDate.format("MMM DD, YYYY"));
    setEndDateToShow(today.format("MMM DD, YYYY"));

    submit("/actions/refresh/?", {
      method: "GET",
      body: {
        from: startFormat,
        to: endFormat,
        activeRange: tab,
      },
    });
  };

  const openFilterModal = () => {
    const filters = exerciseBlockData.map((exercise) => exercise.name);

    submit("/actions/refresh/?", {
      method: "GET",
      body: {
        "modal[type]": "filter-block-review-modal",
        filtersModal: JSON.stringify(filters),
      },
    });
  };

  const removeFilter = (type) => {
    const newFiltersSet = JSON.parse(query.exerciseFilters).filter(
      (exercise) => exercise !== type
    );
    submit(`/actions/refresh/?`, {
      method: "GET",
      body: {
        exerciseFilters:
          newFiltersSet.length > 0 ? JSON.stringify(newFiltersSet) : "",
      },
    });
  };

  const clearAllFilters = () => {
    submit(`/actions/refresh/?`, {
      method: "GET",
      body: {
        exerciseFilters: "",
        modal: "",
        filtersModal: "",
      },
    });
  };

  return (
    <main role="reports-wrapper" className="block-review">
      <NavigationComponent
        query={query}
        text="Calendar Day View"
        pathName={pathname}
      />

      <article role="report-content" id="block-review">
        <header>
          <h2>Block Review</h2>

          <div>
            {query.exerciseFilters ? (
              <FilterChips
                filters={
                  query.exerciseFilters
                    ? JSON.parse(query.exerciseFilters).map((filter) => {
                        return { text: filter, id: filter };
                      })
                    : []
                }
                clearAllFilters={clearAllFilters}
                clearFilter={removeFilter}
              />
            ) : null}
            <button
              data-button-type="secondary"
              sz="medium"
              onClick={openFilterModal}
            >
              <SvgIcon src="/img/new-icons/filter-white.svg" />
              <b>Filter</b>
            </button>
          </div>
        </header>

        <div className="Date-Range">
          <div role="calendar-date-selectors">
            <DateInput
              name="from"
              placeholder="Start Date"
              value={startDateToShow}
              max={query.to}
              onChange={(date) => handleDateChange(date, "from")}
            />
            <span>to</span>
            <DateInput
              name="to"
              placeholder="End Date"
              value={endDateToShow}
              min={query.from}
              onChange={(date) => handleDateChange(date, "to")}
            />
          </div>

          <div
            className="right-side"
            id="tabsSection"
            style={{ display: "flex", gap: "4px" }}
          >
            {[4, 8, 12, 16, 20, 24].map((weeks) => (
              <button
                data-button-type={tabState === weeks ? "primary" : "secondary"}
                id={`${weeks}-weeks-tab`}
                key={`${weeks}-weeks-tab`}
                onClick={() => handleActiveTab(weeks)}
              >
                {weeks} weeks
              </button>
            ))}
          </div>
        </div>

        <div className="block-review-legend">
          <div className="block-review-legend-item">
            <div className={STATUS_CLASS_NAME.completed}></div>
            <span>Completed</span>
          </div>
          <div className="block-review-legend-item">
            <div className={STATUS_CLASS_NAME.missed}></div>
            <span>Missed</span>
          </div>
          <div className="block-review-legend-item">
            <div className={STATUS_CLASS_NAME.incomplete}></div>
            <span>Incomplete</span>
          </div>
        </div>

        <section role="table-wrapper">
          {!!exerciseBlockDataToShow.length ? (
            <table role="table-wrapper">
              <thead role="table-header">
                <tr>
                  <th>
                    <span>Exercise Name</span>
                  </th>
                  <th style={{ left: left - 32 }}>
                    <span>Sets</span>
                  </th>
                  {periods.map((period) => (
                    <th key={`${period}-period-item`} colSpan={2}>
                      <span>{period}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody role="table-body">
                {exerciseBlockDataToShow.map((exerciseData, index) => (
                  <tr
                    key={`${exerciseData.name}-row`}
                    className="block-review-target-row"
                  >
                    {/* Exercise Name Column */}
                    <td key={`${exerciseData.name}-${index}-name`} ref={ref}>
                      <p>{_.upperFirst(exerciseData.name)}</p>
                    </td>
                    {/* Sessions and Sets Column if only 1 Session */}
                    {exerciseData.sessions === 1 && (
                      <td className="session-cell" style={{ left: left - 32 }}>
                        <div className="session-titles">
                          {_.times(exerciseData.numberOfSets[0]).map(
                            (index) => (
                              <p
                                key={`${exerciseData.name}-set-${exerciseData.numberOfSets[0]}-${index}`}
                              >
                                SET {index + 1}
                              </p>
                            )
                          )}
                        </div>

                        <div className="extra-session">
                          <p className="s1">Metrics</p>
                        </div>
                        <div className="session-titles">
                          <p>Max weight</p>
                          <p>Top e1rm</p>
                          <p>Volume</p>
                        </div>
                      </td>
                    )}
                    {/* Sessions and Sets Column if more than 1 session */}
                    {exerciseData.sessions > 1 && (
                      <td className="session-cell" style={{ left: left - 32 }}>
                        {_.times(exerciseData.sessions).map((sessionIndex) => (
                          <React.Fragment
                            key={`${sessionIndex}-set-extra-session`}
                          >
                            <div className="extra-session">
                              <p className="s1">Session {sessionIndex + 1}</p>
                            </div>
                            <div className="session-titles">
                              {_.times(
                                exerciseData.numberOfSets[sessionIndex]
                              ).map((setIndex) => (
                                <p
                                  key={`${exerciseData.name}-set-${exerciseData.numberOfSets[sessionIndex]}-${setIndex}`}
                                >
                                  SET {setIndex + 1}
                                </p>
                              ))}
                            </div>
                          </React.Fragment>
                        ))}
                        <div className="extra-session">
                          <p className="s1">Metrics</p>
                        </div>
                        <div className="session-titles">
                          <p>Max weight</p>
                          <p>Top e1rm</p>
                          <p>Volume</p>
                        </div>
                      </td>
                    )}
                    {/* Targets */}
                    {Object.entries(exerciseData.periods)
                      .sort((a, b) => {
                        return (
                          new Date(a[1].startOfWeek) -
                          new Date(b[1].startOfWeek)
                        );
                      })
                      .map(([periodKey]) => (
                        <td
                          key={`${periodKey}-${exerciseData.name}`}
                          colSpan={2}
                          className="targets-td"
                        >
                          <div className="targets-div">
                            {!!exerciseData.periods[periodKey].hasData &&
                              _.times(exerciseData.sessions).map(
                                (sessionIndex) => (
                                  <div
                                    className={
                                      exerciseData.sessions > 1
                                        ? "block-review-extra-session-container"
                                        : "block-review-session-container"
                                    }
                                    key={`${periodKey}-${exerciseData.name}-${sessionIndex}`}
                                  >
                                    {exerciseData.sessions > 1 && (
                                      <p className="blank-target">&nbsp;</p>
                                    )}
                                    {Object.keys(
                                      exerciseData.periods[periodKey].outcomes[
                                        sessionIndex + 1
                                      ] || {}
                                    ).map((outcomeIndex) => {
                                      const { status, comment, target, video } =
                                        exerciseData.periods[periodKey]
                                          .outcomes[sessionIndex + 1][
                                          outcomeIndex
                                        ];

                                      return (
                                        outcomeIndex >= 0 && (
                                          <div
                                            className="block-review-target-container"
                                            key={`${periodKey}-${exerciseData.name}-${sessionIndex}-${outcomeIndex}`}
                                          >
                                            {target ? (
                                              <div className="block-review-target">
                                                {status && (
                                                  <div
                                                    className={
                                                      STATUS_CLASS_NAME[status]
                                                    }
                                                  ></div>
                                                )}
                                                <p>{target}</p>
                                              </div>
                                            ) : (
                                              <p>&nbsp;</p>
                                            )}

                                            <div className="block-review-icon-container">
                                              {comment && (
                                                <div className="block-review-icon">
                                                  <ToolTip
                                                    id={`${exerciseData.name}-${periodKey}-${sessionIndex}-${outcomeIndex}-comment`}
                                                    body={comment}
                                                  >
                                                    <SvgIcon src="/img/new-icons/vuesax-linear-message-text.svg" />
                                                  </ToolTip>
                                                </div>
                                              )}
                                              {video && (
                                                <a
                                                  className="block-review-icon"
                                                  href={`/actions/refresh?modal[type]=exercise-set-video&mediaId=${video.videoId}&originalId=${video.videoOriginal}&athleteId=${athleteId}&uploaderId=${video.uploaderId}`}
                                                >
                                                  <SvgIcon src="/img/new-icons/vuesax-linear-play.svg" />
                                                </a>
                                              )}
                                            </div>
                                          </div>
                                        )
                                      );
                                    })}
                                  </div>
                                )
                              )}

                            {/* Metrics */}
                            <p className="blank-target">&nbsp;</p>
                            <div className="session-titles">
                              <p className="s1">
                                {!!exerciseData.periods[periodKey].hasData &&
                                  `${
                                    _.round(
                                      fromMetric(
                                        exerciseData.periods[periodKey]
                                          .maxWeight,
                                        units
                                      ),
                                      precision
                                    ) || " - "
                                  }${unitsLabel}`}
                              </p>
                              <p className="s1">
                                {!!exerciseData.periods[periodKey].hasData &&
                                  `${
                                    _.round(
                                      fromMetric(
                                        exerciseData.periods[periodKey].topE1rm,
                                        units
                                      ),
                                      precision
                                    ) || " - "
                                  }${unitsLabel}`}
                              </p>
                              <p className="s1">
                                {!!exerciseData.periods[periodKey].hasData &&
                                  `${
                                    _.round(
                                      fromMetric(
                                        exerciseData.periods[periodKey].volume,
                                        units
                                      ),
                                      precision
                                    ) || " - "
                                  }${unitsLabel}`}
                              </p>
                            </div>
                          </div>
                        </td>
                      ))}
                  </tr>
                ))}

                {!!metricsDataKeys.length && (
                  <>
                    {/* Volume */}
                    <tr className="block-review-metrics-row">
                      <td colSpan={2}>
                        <p>Volume</p>
                      </td>

                      {metricsDataKeys.map((metricKey) => (
                        <td key={`metric-volume-${metricKey}`} colSpan={2}>
                          <p>
                            {_.round(
                              fromMetric(
                                metricsBlockData[metricKey].volume,
                                units
                              ),
                              precision
                            ) || " - "}
                            {unitsLabel}
                          </p>
                        </td>
                      ))}
                    </tr>
                    {/* Sets by muscle group */}
                    <tr className="block-review-metrics-row">
                      <td colSpan={2}>
                        <p>Sets by muscle group</p>
                      </td>

                      {metricsDataKeys.map((metricKey) => (
                        <td key={`metric-sets-${metricKey}`} colSpan={2}>
                          {MUSCLE_GROUP_ARRAY.map((muscle) => {
                            if (muscle !== "None") {
                              return (
                                <div
                                  key={`metric-sets-${metricKey}-${muscle}`}
                                  style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <span>{muscle}</span>
                                  <span>
                                    {metricsBlockData[metricKey].sets[muscle] ||
                                      "-"}
                                  </span>
                                </div>
                              );
                            }
                          })}
                        </td>
                      ))}
                    </tr>
                    {/* Reps by muscle group */}
                    <tr className="block-review-metrics-row">
                      <td colSpan={2}>
                        <p>Reps by muscle group</p>
                      </td>

                      {metricsDataKeys.map((metricKey) => (
                        <td key={`metric-reps-${metricKey}`} colSpan={2}>
                          {MUSCLE_GROUP_ARRAY.map((muscle) => {
                            if (muscle !== "None") {
                              return (
                                <div
                                  key={`metric-reps-${metricKey}-${muscle}`}
                                  style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <span>{muscle}</span>
                                  <span>
                                    {metricsBlockData[metricKey].reps[muscle] ||
                                      "-"}
                                  </span>
                                </div>
                              );
                            }
                          })}
                        </td>
                      ))}
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          ) : (
            <div>
              <p className="p1"> No exercise data found</p>
            </div>
          )}
        </section>
      </article>
    </main>
  );
};

const STATUS_CLASS_NAME = {
  completed: "circle-status completed",
  missed: "circle-status missed",
  incomplete: "circle-status incomplete",
};

export default BlockReview;
